import {
  Box,
  CheckboxField,
  DateField,
  Label,
  NumberField,
  SingleSelectField,
  TextField,
  ToggleButtonGroupField,
  HStack,
  useFormContext,
  useWatch,
  Heading,
} from '@medely/ui-kit';
import React, { useEffect } from 'react';
import {
  EMPLOYMENT_TYPE_OPTIONS,
  NON_CLINICAL_EMPLOYMENT_TYPE_OPTIONS,
  YES_NO_OPTIONS,
} from '../constants';
import { ContentLoader } from '@mp/components/ContentLoader';
import { useAllPositionsForWorkHistoryModal, useLocationTypes, useStates } from '../graphql';

type WorkHistoryFormProps = {
  isClinical: boolean;
  isEdit: boolean;
};

export const WorkHistoryForm = ({ isClinical, isEdit }: WorkHistoryFormProps) => {
  const isWeb = typeof document !== 'undefined';
  const { setValue, clearErrors } = useFormContext();

  const { allPositions, isLoading: isLoadingAllPositions } = useAllPositionsForWorkHistoryModal(
    true,
    isClinical,
  );
  const positionOptions = isClinical
    ? allPositions?.map(({ id, display_name }) => ({
        value: id as number,
        label: display_name as string,
      }))
    : undefined;

  const { locationTypes, isLoading: isLoadingLocationTypes } = useLocationTypes(isClinical);
  const locationTypeOptions = isClinical
    ? locationTypes?.map(({ id, label }) => ({
        value: id as number,
        label: label as string,
      }))
    : undefined;

  const { states, isLoading: isLoadingStates } = useStates();
  const statesOptions = states.map(({ abbreviation, id }) => ({
    value: id as number,
    label: abbreviation as string,
  }));

  const isLoading =
    isLoadingStates || (isClinical && (isLoadingAllPositions || isLoadingLocationTypes));

  const currentlyWorkHere = useWatch({ name: 'currently_work_here' });
  const displaysFloatingDetails = useWatch({ name: 'floating' });
  const selectedLocationTypeId = useWatch({ name: 'location_type_id' });
  const isHospital = selectedLocationTypeId === 8;
  const endDate = useWatch({ name: 'end_date' });

  useEffect(() => {
    if (currentlyWorkHere) {
      setValue('end_date', null);
      clearErrors('end_date');
    } else {
      setValue('end_date', endDate);
      clearErrors('end_date');
    }
  }, [currentlyWorkHere, endDate]);

  useEffect(() => {
    if (!isHospital) {
      setValue('location_trauma_level', null);
      setValue('location_number_of_beds', null);
      setValue('location_is_teaching', null);
      clearErrors('location_trauma_level');
      clearErrors('location_number_of_beds');
      clearErrors('location_is_teaching');
    }
  }, [isHospital, setValue, clearErrors]);

  return (
    <ContentLoader loading={isLoading}>
      <>
        {!isWeb && (
          <Box py={1}>
            <Heading size="xs">
              {isClinical
                ? `${isEdit ? 'Edit' : 'Add'} clinical experience`
                : `${isEdit ? 'Edit' : 'Add'} non-clinical experience`}
            </Heading>
          </Box>
        )}
        {positionOptions && isClinical && (
          <>
            <SingleSelectField
              label=""
              placeholder="Position *"
              name="position_id"
              options={positionOptions}
            />
          </>
        )}
        <Box py={0.5} />
        <TextField name="position_title" placeholder={isClinical ? 'Job title' : 'Job title *'} />
        <Box py={0.5} />
        {isClinical && (
          <>
            <TextField name="unit" placeholder="Unit *" />
            <Box py={2} />
            <ToggleButtonGroupField
              label="Did you float to other units?"
              name="floating"
              options={YES_NO_OPTIONS}
            />
            {displaysFloatingDetails && (
              <>
                <Box py={0.5} />
                <TextField
                  name="floating_details"
                  rows={4}
                  multiline
                  placeholder="Units and hrs/week floated"
                  helperText="List the hours per week you worked in each unit"
                />
              </>
            )}
          </>
        )}
        <CheckboxField label="I’m currently working in this position" name="currently_work_here" />
        <Box py={1} />
        <HStack gap={2}>
          <Box flex={1}>
            <DateField
              name="start_date"
              label="Start date *"
              labelStyle="inline"
              parseValue={(value) => (value ? value : null)}
            />
          </Box>
          <Box flex={1}>
            <DateField
              name="end_date"
              label="End date *"
              labelStyle="inline"
              disabled={!!currentlyWorkHere}
              parseValue={(value) => (value ? value : null)}
            />
          </Box>
        </HStack>
        <Box py={0.5} />
        <HStack justifyContent="space-between">
          <Box width={isClinical ? '60%' : '100%'}>
            <SingleSelectField
              label=""
              placeholder="Employment type *"
              name="employment_type"
              options={isClinical ? EMPLOYMENT_TYPE_OPTIONS : NON_CLINICAL_EMPLOYMENT_TYPE_OPTIONS}
            />
          </Box>
          {isClinical && (
            <Box width="36%">
              <NumberField name="hours_per_week" placeholder="Hrs/week" />
            </Box>
          )}
        </HStack>
        <Box py={0.5} />
        <TextField
          name="description"
          rows={4}
          multiline
          placeholder="Description of job duties *"
        />
        <Box py={2}>
          <Label size="l">{isClinical ? 'Facility details' : 'Company details'}</Label>
        </Box>
        <TextField
          labelStyle="inline"
          name="organization_name"
          placeholder={isClinical ? 'Facility name *' : 'Company name *'}
        />
        {isClinical && (
          <>
            <Box py={0.5} />
            <SingleSelectField
              label=""
              placeholder="Facility type *"
              name="location_type_id"
              options={locationTypeOptions || []}
            />
            {isHospital && (
              <>
                <Box py={0.5} />
                <HStack justifyContent="space-between">
                  <Box width="58%">
                    <SingleSelectField
                      label=""
                      placeholder="Trauma level"
                      name="location_trauma_level"
                      options={[
                        { value: 1, label: 'Level I' },
                        { value: 2, label: 'Level II' },
                        { value: 3, label: 'Level III' },
                        { value: 4, label: 'Level IV' },
                        { value: 5, label: 'Level V' },
                      ]}
                    />
                  </Box>
                  <Box width="38%">
                    <NumberField name="location_number_of_beds" placeholder="# of beds" />
                  </Box>
                </HStack>
                <Box py={1} />
                <ToggleButtonGroupField
                  label="Is the facility a teaching hospital?"
                  name="location_is_teaching"
                  options={[
                    { value: true, label: 'Yes' },
                    { value: false, label: 'No' },
                  ]}
                />
              </>
            )}
          </>
        )}
        <Box py={0.5} />
        <HStack justifyContent="space-between" mb={isWeb ? 6 : 2}>
          <Box width="60%">
            <TextField name="location" placeholder="City *" />
          </Box>
          <Box width="36%">
            <SingleSelectField
              label=""
              placeholder="State *"
              name="state_id"
              options={statesOptions}
            />
          </Box>
        </HStack>
        <Box py={1} />
      </>
    </ContentLoader>
  );
};
